import { Box, IconButton, Grid } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import MobileTicketMessageList from './MobileTicketMessageList';
import Iconify from '../../../../../reusable-components/iconify';
import MobileTicketMessageInput from './MobileTicketMessageInput';
import { useSelector } from '../../../../../redux/store';
import MobileTicketUsers from './MobileTicketUsers';
import MobileTicketInfo from './MobileTicketInfo';
import { getTicketData, getTicketMessages, getFacilitiesAndUsersList, setIsAddingNewTicket } from '../../../../../redux/slices/tickets';
import SubticketsMenu from './SubticketsMenu';
import { isValidDate } from '../../../../../utils/validate';
import TicketSubject from './TicketSubject';
import { useTabs } from '../../../../../context/TabContext';
import { requestGridScrollRestore } from '../../../../../hooks/useGridScrollRestore';

export default function MobileTicketView() {
  const navigate = useNavigate();
  const location = useLocation();
  const subticketId = new URLSearchParams(location.search).get('subticket');
  const { id = '' } = useParams();
  const [isTicketInfoOpen, setIsTicketInfoOpen] = useState(false);
  const [isAddingSubticket, setIsAddingSubticket] = useState(false);
  const { subtickets, mainTicket, users: mainTicketUsers, allTicketUsers } = useSelector((state) => state.tickets.data.ticketInfoById[id] || {});  
  const { facilities } = useSelector((state) => state.tickets.data);
  const { ticketMessages, users, ticketUsers } = useSelector(
    (state) => state.tickets.data.ticketById[subticketId || id] || {}
  );
  const [searchParams] = useSearchParams();
  const tabs = useTabs();

  const messagesRef = useRef(null);

  const ticketSubject = useMemo(() => {
    const subject = mainTicket?.extraInfo || '';
    const subticket = subtickets?.find((subticket) => subticket.id === parseInt(subticketId, 10));
    return subticket?.extraInfo || subject;
  }, [mainTicket, subtickets, subticketId]);

  const facility = useMemo(() => facilities?.find(f => f.id === mainTicket?.facilityId), [facilities, mainTicket]);

  const goBackToTickets = () => {
    if (!mainTicket?.complete) {
      requestGridScrollRestore('mobile-open-tickets', tabs?.activeTab?.key);
      navigate(`/dashboard/tickets`);
    } else {
      requestGridScrollRestore('mobile-completed-tickets', tabs?.activeTab?.key);
      const start = searchParams.get('start');
      const end = searchParams.get('end');
      let startParam;
      let endParam;
      if(isValidDate(start) && isValidDate(end)) {
        startParam = new Date(start).toISOString();
        endParam = new Date(end).toISOString();
      }
      else {
        startParam = new Date(new Date().setDate(new Date().getDate() - 29)).toISOString();
        endParam = new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString();
      }
      navigate(
        `/dashboard/tickets?status=complete&start=${startParam}&end=${endParam}`
      );
    }
  };

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setIsAddingNewTicket(false));
  }, [])

  useEffect(() => {
    dispatch(getFacilitiesAndUsersList());
    dispatch(getTicketData(id, (parentId) => navigate(`/dashboard/tickets/${parentId}?subticket=${id}`)));
    dispatch(getTicketMessages({ id: subticketId || id }));
  }, [id, subticketId]);

  const containerStyle = {
    p: 1, 
    borderRadius: 2,
    backgroundColor: 'white !important',
  }

  useEffect(() => {
    if (ticketMessages && ticketMessages.length > 0) {
      messagesRef.current?.scrollTo({
        top: messagesRef.current.scrollHeight,
        behavior: 'auto'
      });
    }
  }, [ticketMessages, allTicketUsers]);

  return (
    <>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }}>
      <Box>
        <Grid container direction="row">
          <Grid item xs={3}>
            <IconButton
              color="primary"
              onClick={goBackToTickets}
            >
              <Iconify icon="mdi:chevron-left" />
            </IconButton>
          </Grid>
          <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TicketSubject subject={ticketSubject} />
          </Grid>
          <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 1 }}>
            <SubticketsMenu isTicketInfoOpen={isTicketInfoOpen} />
          </Grid>
        </Grid>
        <Box sx={containerStyle}>
          <MobileTicketUsers users={users?.map(u => ({ id: u.id, value: u.userName || u.userEmail, email: u.userEmail})) || []} />
        </Box>
      </Box>
      <Box sx={{
        ...containerStyle,
        flex: 1,
        minHeight: 0,
        px: 1.5,
        mt: 1,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }} ref={messagesRef}>
          <MobileTicketMessageList />
        </Box>
        <Box sx={{ flexShrink: 0, mt: 1 }}>
          <MobileTicketMessageInput
            users={ticketUsers}
            messagesRef={messagesRef}
            setIsTicketInfoOpen={setIsTicketInfoOpen}
            setIsAddingSubticket={setIsAddingSubticket}
          />
        </Box>
      </Box>
    </Box>
    <MobileTicketInfo
      facility={facility}
      possibleUsers={mainTicketUsers}
      ticketUsers={users}
      setIsTicketInfoOpen={setIsTicketInfoOpen}
      isAddingSubticket={isAddingSubticket}
      setIsAddingSubticket={setIsAddingSubticket}
      ticketSubject={ticketSubject}
      isTicketInfoOpen={isTicketInfoOpen}
      setExternalTicketSubject={() => {}}
    />
    </>
  );
}